import type { BillingInterval, BillingPlan } from 'definitions/billing-plans';
import type { CreateCompanyPayload } from './useAdminBilling';
import { useState } from 'react';

export type AdminCompanyFormState = {
  name: string;
  type: CreateCompanyPayload['type'];
  billingMode: CreateCompanyPayload['billingMode'];
  email: string;
  firstName: string;
  lastName: string;
  plan: BillingPlan;
  interval: BillingInterval;
};

const EMPTY_FORM: AdminCompanyFormState = {
  name: '',
  type: 'Client Account',
  billingMode: 'invoice',
  email: '',
  firstName: '',
  lastName: '',
  plan: 'unlimited',
  interval: 'year',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type BuildResult = { payload: CreateCompanyPayload; blockingReason?: undefined } | { blockingReason: string };

/**
 * The rules admin-billing/create-company.ts applies, checked here first so the Create button
 * can say what is missing instead of the server's 400 doing it after the fact. An agency sends
 * no plan: it has a fee, and each of its clients gets a plan when it is added.
 */
export function buildCompanyPayload(form: AdminCompanyFormState): BuildResult {
  const name = form.name.trim();
  if (!name) return { blockingReason: 'Enter the company name' };

  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  if (!firstName || !lastName) return { blockingReason: "Enter the owner's first and last name" };

  const email = form.email.trim();
  if (!EMAIL_PATTERN.test(email)) return { blockingReason: "Enter the owner's email address" };

  const isAgency = form.type === 'Agency';
  return {
    payload: {
      name,
      type: form.type,
      billingMode: form.billingMode,
      owner: { email, firstName, lastName },
      ...(isAgency ? {} : { plan: form.plan, interval: form.interval }),
    },
  };
}

type UseAdminCompanyFormParams = { createCompany: (payload: CreateCompanyPayload) => Promise<unknown> };

/** The Create company dialog: open state, the fields, validation and submit. */
export const useAdminCompanyForm = ({ createCompany }: UseAdminCompanyFormParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<AdminCompanyFormState>(EMPTY_FORM);

  const setField = <K extends keyof AdminCompanyFormState>(key: K, value: AdminCompanyFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const result = buildCompanyPayload(form);

  const open = () => {
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const close = () => {
    setForm(EMPTY_FORM);
    setIsOpen(false);
  };

  const submit = async () => {
    if (!('payload' in result) || !result.payload) return;
    try {
      await createCompany(result.payload);
      close();
    } catch {
      // Toasted by the mutation; the dialog stays open with what was typed.
    }
  };

  return { isOpen, open, close, form, setField, blockingReason: result.blockingReason, submit };
};

export type AdminCompanyFormManager = ReturnType<typeof useAdminCompanyForm>;
